import { useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  createFilterOptions,
  ListItemButton,
  ListItemText,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import { PolicyPositionOption } from '@/hooks/usePolicyPositionOptions'
import { useGetAuthUserList } from '@/service-library/hooks/auth-user'
import { AuthUser } from '@/types/users'
import { ApproverRef, approverRefKey } from '@/utils/approver-entries'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'

/**
 * A position and a person are picked from the same search box, so when
 * building a policy you never have to decide up front which kind of approver
 * a slot on the list will take.
 */
type ApproverOption =
  | {
      group: 'Positions'
      key: string
      ref: ApproverRef
      option: PolicyPositionOption
    }
  | { group: 'People'; key: string; ref: ApproverRef; user: AuthUser }

const GROUP_HINTS: Record<string, string> = {
  Positions: "from this project's catalog",
  People: 'named users',
}

function optionName(option: ApproverOption) {
  return option.group === 'Positions'
    ? option.option.position.name
    : option.user.name
}

function optionDetail(option: ApproverOption) {
  if (option.group === 'People') return option.user.email ?? ''
  const { lookupLabel, ruleSummary } = option.option
  return [lookupLabel, ruleSummary].filter(Boolean).join(' · ')
}

// Search matches the name, since that's how an approver is shown on the
// policy. It also matches the detail line (email, or a position's rule
// summary), so someone who only knows an approver by that can still find them.
const filterOptions = createFilterOptions<ApproverOption>({
  stringify: (option) => `${optionName(option)} ${optionDetail(option)}`,
})

type ApproverSearchInputProps = {
  positionOptions: PolicyPositionOption[]
  /** Already on the policy, so they can't be added twice. */
  pickedRefs: ApproverRef[]
  onPick: (ref: ApproverRef) => void
  disabled?: boolean
  sx?: SxProps<Theme>
}

export default function ApproverSearchInput({
  positionOptions,
  pickedRefs,
  onPick,
  disabled = false,
  sx,
}: ApproverSearchInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [enabled, setEnabled] = useState(false)

  const { users, isPending } = useGetAuthUserList({
    enabled,
    filters: { fields__only: 'id,name,email', status__in: 'active' },
  })

  const pickedKeys = useMemo(
    () => new Set(pickedRefs.map(approverRefKey)),
    [pickedRefs],
  )

  // Positions first, then people: MUI repeats a group heading whenever the
  // options aren't already grouped together.
  const options = useMemo<ApproverOption[]>(() => {
    const positionEntries = positionOptions.map(
      (option): ApproverOption => ({
        group: 'Positions',
        key: `position:${option.position.id}`,
        ref: { kind: 'position', positionId: option.position.id },
        option,
      }),
    )

    const userEntries = users.flatMap((user): ApproverOption[] =>
      user.id
        ? [
            {
              group: 'People',
              key: `user:${user.id}`,
              ref: { kind: 'user', userId: user.id },
              user,
            },
          ]
        : [],
    )

    return [...positionEntries, ...userEntries]
  }, [positionOptions, users])

  const isPickable = (option: ApproverOption) =>
    !pickedKeys.has(approverRefKey(option.ref))

  return (
    <Autocomplete<ApproverOption, false, false, false>
      options={options}
      loading={isPending}
      filterOptions={filterOptions}
      groupBy={(option) => option.group}
      autoHighlight
      openOnFocus
      clearOnEscape
      clearOnBlur
      disableCloseOnSelect
      disabled={disabled}
      sx={sx}
      // Picking an approver adds them to the list below; it isn't a value
      // this input holds on to. Always passing null as the value keeps the
      // picked name out of the box, and lets the same option be picked again
      // later if it's removed from the list.
      value={null}
      inputValue={inputValue}
      onInputChange={(_event, value, reason) => {
        setInputValue(reason === 'input' ? value : '')
      }}
      onChange={(_event, option) => {
        // Keyboard selection can still reach an option the picker disabled, so
        // this is what actually blocks a duplicate.
        if (option && isPickable(option)) onPick(option.ref)
        setInputValue('')
      }}
      // Without this the option is only styled as disabled — it stays
      // announced as selectable and Enter still picks it.
      getOptionDisabled={(option) => !isPickable(option)}
      onOpen={() => setEnabled(true)}
      getOptionLabel={optionName}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add an approver"
          // Only mentions positions if there are some to pick from.
          placeholder={
            positionOptions.length > 0
              ? 'Add a position or a person...'
              : 'Search users...'
          }
          variant="outlined"
        />
      )}
      renderGroup={({ key, group, children }) => (
        <Box key={key} component="li" sx={{ listStyle: 'none' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.75,
              px: 2,
              pt: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', fontWeight: 700 }}
            >
              {group}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {GROUP_HINTS[group]}
            </Typography>
          </Box>
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {children}
          </Box>
        </Box>
      )}
      renderOption={(props, option) => (
        <ListItemButton
          component="li"
          {...props}
          key={option.key}
          // MUI would otherwise mark a disabled option as selected. It isn't
          // selected, it's just unavailable, so force this to false.
          aria-selected={false}
          disabled={!isPickable(option)}
          dense
          sx={{ gap: 1.5 }}
        >
          {option.group === 'Positions' ? (
            <PositionAvatar />
          ) : (
            <UserAvatar name={option.user.name} />
          )}
          <ListItemText
            primary={optionName(option)}
            secondary={optionDetail(option)}
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
            sx={{ my: 0 }}
          />
        </ListItemButton>
      )}
    />
  )
}
